import { useEffect, useState } from 'react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { MarkdownEditor } from '@/components/ui/markdown-editor'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import {
  type AgentVersion,
  useActivateAgentVersion,
  useAgentVersions,
  useCreateAgentVersion,
  useUpdateAgentVersion,
} from '../../../../../models/agentAdmin'

function groupBySlug(versions: AgentVersion[]): Record<string, AgentVersion[]> {
  const groups: Record<string, AgentVersion[]> = {}
  for (const v of versions) {
    const slug = v.agentSlug || 'bob'
    ;(groups[slug] ??= []).push(v)
  }
  return groups
}

export function AgentsTab() {
  const { data: versions, isLoading } = useAgentVersions()
  const updateMutation = useUpdateAgentVersion()
  const createMutation = useCreateAgentVersion()
  const activateMutation = useActivateAgentVersion()

  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [editedPrompt, setEditedPrompt] = useState('')
  const [editedNotes, setEditedNotes] = useState('')
  const [newName, setNewName] = useState('')
  const [hasChanges, setHasChanges] = useState(false)

  const selected = versions?.find((v) => v.id === selectedId) ?? null
  const grouped = versions ? groupBySlug(versions) : {}
  const slugs = Object.keys(grouped).sort()

  function selectVersion(version: AgentVersion) {
    setSelectedId(version.id)
    setEditedPrompt(version.systemPrompt)
    setEditedNotes(version.notes)
    setHasChanges(false)
  }

  useEffect(() => {
    if (versions?.length && !selectedId) {
      const active = versions.find((v) => v.isActive) ?? versions[0]
      if (active) selectVersion(active)
    }
  }, [versions]) // eslint-disable-line react-hooks/exhaustive-deps

  function handleSave() {
    if (!selected) return
    updateMutation.mutate(
      { id: selected.id, systemPrompt: editedPrompt, notes: editedNotes },
      { onSuccess: () => setHasChanges(false) }
    )
  }

  function handleClone() {
    if (!selected || !newName.trim()) return
    createMutation.mutate(
      {
        name: newName.trim(),
        systemPrompt: editedPrompt,
        notes: editedNotes,
        agentSlug: selected.agentSlug,
      },
      {
        onSuccess: (created) => {
          selectVersion(created)
          setNewName('')
        },
      }
    )
  }

  if (isLoading)
    return (
      <div className="grid grid-cols-[260px_1fr] gap-6">
        <div className="space-y-3">
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
        <Skeleton className="h-64 w-full" />
      </div>
    )

  return (
    <div className="grid grid-cols-[260px_1fr] gap-6">
      {/* Version list grouped by agent */}
      <div className="space-y-3">
        {slugs.map((slug) => (
          <div key={slug} className="space-y-1">
            <h3 className="text-sm font-medium text-gray-500 uppercase">{slug}</h3>
            {grouped[slug].map((v) => (
              <button
                key={v.id}
                onClick={() => selectVersion(v)}
                className={`bg-surface w-full rounded-md border px-3 py-2 text-left text-sm transition-colors ${
                  selected?.id === v.id ? 'border-blue-500 bg-blue-50' : 'hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium">{v.name}</span>
                  {v.isActive && (
                    <Badge variant="default" className="text-xs">
                      Active
                    </Badge>
                  )}
                </div>
                <span className="text-xs text-gray-500">v{v.versionNumber}</span>
              </button>
            ))}
          </div>
        ))}
        {!slugs.length && <p className="text-sm text-gray-500">No agents found.</p>}
      </div>

      {/* Editor */}
      {selected ? (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>
                {selected.name}
                <Badge variant="outline" className="ml-2 text-xs">
                  {selected.agentSlug}
                </Badge>
              </CardTitle>
              <div className="flex gap-2">
                {!selected.isActive && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => activateMutation.mutate(selected.id)}
                    disabled={activateMutation.isPending}
                  >
                    Set Active
                  </Button>
                )}
                <Button
                  size="sm"
                  onClick={handleSave}
                  disabled={!hasChanges || updateMutation.isPending}
                  loading={updateMutation.isPending ? 'Saving...' : undefined}
                >
                  Save
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="mb-1 block text-sm font-medium">System Prompt</label>
              <MarkdownEditor
                value={editedPrompt}
                onChange={(value) => {
                  setEditedPrompt(value)
                  setHasChanges(true)
                }}
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">Notes</label>
              <Textarea
                value={editedNotes}
                onChange={(e) => {
                  setEditedNotes(e.target.value)
                  setHasChanges(true)
                }}
                rows={3}
              />
            </div>
            <div className="border-t pt-4">
              <label className="mb-1 block text-sm font-medium">Clone as new version</label>
              <div className="flex gap-2">
                <Input
                  placeholder="New version name"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                />
                <Button
                  variant="outline"
                  onClick={handleClone}
                  disabled={!newName.trim() || createMutation.isPending}
                  loading={createMutation.isPending ? 'Creating...' : undefined}
                >
                  Clone
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <p className="text-gray-500">No agent versions found.</p>
      )}
    </div>
  )
}
